import { Injectable } from '@angular/core';
import {
  extractSsbdStatusCodes,
  extractDeviceStatusValue,
} from '@app/cuss/embross/utils/ssbd-status-parser';
import {
  SsbdEmergencyCode,
  SSBD_EMERGENCY_OPEN_CODES,
  SSBD_EMERGENCY_CLOSE_CODES,
  SSBD_HARDWARE_ERROR_THRESHOLD,
} from '@app/enums/ssbd-emergency.enum';
import { OfflineConnectivityModalService } from '@app/services/offline-connectivity-modal.service';
import { CussService } from '@app/services/cuss.service';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';

/**
 * Orchestrates the SSBD emergency modal lifecycle using a state machine.
 *
 * State fields:
 * - `emergencyPressed`: true after receiving code 139. While true, subsequent 302s
 *   keep the modal open (already showing) without counting towards the threshold.
 * - `hardwareErrorCount`: consecutive observations of 302 via `statusChange` or
 *   `statusIsOK` without a preceding 139. The modal opens when this reaches
 *   SSBD_HARDWARE_ERROR_THRESHOLD (3).
 *
 * Behavior summary:
 * - 139 → open modal immediately, by any channel. No OOS.
 * - 305 → open modal immediately + OOS, by any channel.
 * - 302 with emergencyPressed → modal already open, no count, no OOS.
 * - 302 without emergencyPressed → count via statusChange/statusIsOK;
 *   open modal at threshold. Never OOS.
 * - Any other code (via statusChange or statusIsOK) → clear state, close modal.
 * - 122 or NORM in SD field (via unsolicitedMessage) → clear state, close modal.
 *
 * Designed to be called from both:
 * - SbdService.callback (bag flow path — device is enabled)
 * - ssbd.callback.ts shared callback (idle path — device registered via CussService)
 */
@Injectable({
  providedIn: 'root',
})
export class SsbdEmergencyService {
  /** True after receiving code 139 (emergency button pressed). */
  private _emergencyPressed = false;

  /** Consecutive 302 observations via statusChange/statusIsOK without 139. */
  private _hardwareErrorCount = 0;

  constructor(
    private readonly modalService: OfflineConnectivityModalService,
    private readonly cussService: CussService,
    private readonly newRelicEvents: NewRelicEventsService
  ) {}

  // ─── Read-only accessors for testing ────────────────────────────────────────

  get emergencyPressed(): boolean {
    return this._emergencyPressed;
  }

  get hardwareErrorCount(): number {
    return this._hardwareErrorCount;
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Processes a raw unsolicited message from the SSBD device.
   *
   * - 139 / EMCY → open modal immediately, set emergencyPressed flag.
   * - 305 → open modal immediately + OOS.
   * - 302 → return true (suppress generic OOS path) but do NOT count or open.
   * - 122 or SD=NORM → clear state, close modal.
   *
   * @param rawMessage - The raw `#`-delimited unsolicited message string
   * @returns `true` if an emergency code was handled (caller may short-circuit)
   */
  handleUnsolicitedMessage(rawMessage: string | null | undefined): boolean {
    if (!rawMessage) {
      return false;
    }

    const codes = extractSsbdStatusCodes(rawMessage);
    let handled = false;

    for (const code of codes) {
      if (code === SsbdEmergencyCode.EMERGENCY_PRESSED) {
        // 139: open modal immediately, mark emergency pressed
        this._emergencyPressed = true;
        this._hardwareErrorCount = 0;
        this.modalService.show('emergency');
        handled = true;
      } else if (code === SsbdEmergencyCode.NOT_RESPONDING) {
        // 305: open modal immediately + OOS
        this.modalService.show('emergency');
        this.triggerOos(305);
        handled = true;
      } else if (code === SsbdEmergencyCode.EMERGENCY_UNLOCKED) {
        // 302: suppress generic OOS path but do NOT count or open
        handled = true;
      } else if (SSBD_EMERGENCY_CLOSE_CODES.has(code)) {
        // 122: clear state, close modal
        this.clearEmergencyState();
        handled = true;
      }
    }

    // If no emergency codes found, check SD field for NORM to close modal
    if (!handled) {
      const sdValue = extractDeviceStatusValue(rawMessage);
      if (sdValue === 'NORM' && this.modalService.currentVariant === 'emergency') {
        this.clearEmergencyState();
        // Return false — NORM is not an "emergency code" but we did close the modal
      }
    }

    return handled;
  }

  /**
   * Processes a statusChange event code from the SSBD device.
   *
   * This is one of two channels that count 302 towards the threshold
   * (the other is statusIsOK).
   *
   * @param statusCode - The numeric status code from the statusChange event
   * @returns `true` if the code is 139/302/305 (caller should NOT fall through
   *          to the generic 301-310 OOS path). `false` otherwise.
   */
  handleStatusChange(statusCode: number): boolean {
    // 139: open modal immediately
    if (statusCode === SsbdEmergencyCode.EMERGENCY_PRESSED) {
      this._emergencyPressed = true;
      this._hardwareErrorCount = 0;
      this.modalService.show('emergency');
      return true;
    }

    // 305: open modal immediately + OOS
    if (statusCode === SsbdEmergencyCode.NOT_RESPONDING) {
      this.modalService.show('emergency');
      this.triggerOos(305);
      return true;
    }

    // 302: count or keep modal open
    if (statusCode === SsbdEmergencyCode.EMERGENCY_UNLOCKED) {
      if (this._emergencyPressed) {
        // Emergency already active — modal already showing, no count needed
        this.modalService.show('emergency'); // idempotent
      } else {
        this.registerHardwareError('statusChange');
      }
      return true;
    }

    // Any other code: clear state, close modal if open
    if (!SSBD_EMERGENCY_OPEN_CODES.has(statusCode)) {
      this.clearEmergencyState();
    }

    return false;
  }

  /**
   * Processes a statusIsOK event from the SSBD device.
   * The value format is "code,status" e.g. "302,0".
   *
   * This is one of two channels that count 302 towards the threshold
   * (the other is statusChange).
   *
   * @param value - The raw statusIsOK value string (e.g. "302,0")
   * @returns `true` if the code is 139/302/305 (suppresses generic OOS path)
   */
  handleStatusIsOK(value: string | null | undefined): boolean {
    if (!value) return false;
    const parts = String(value).split(',');
    const code = Number(parts[0]);
    if (Number.isNaN(code)) return false;

    // 139: open modal immediately
    if (code === SsbdEmergencyCode.EMERGENCY_PRESSED) {
      this._emergencyPressed = true;
      this._hardwareErrorCount = 0;
      this.modalService.show('emergency');
      return true;
    }

    // 305: open modal immediately + OOS
    if (code === SsbdEmergencyCode.NOT_RESPONDING) {
      this.modalService.show('emergency');
      this.triggerOos(305);
      return true;
    }

    // 302: count towards threshold
    if (code === SsbdEmergencyCode.EMERGENCY_UNLOCKED) {
      if (this._emergencyPressed) {
        this.modalService.show('emergency'); // idempotent
      } else {
        this.registerHardwareError('statusIsOK');
      }
      return true;
    }

    // 122 or any other code: clear state, close modal
    this.clearEmergencyState();
    return false;
  }

  /**
   * Resets all internal state. Call at the start of a new transaction
   * (e.g. SbdService.enable()) to ensure a clean slate.
   */
  resetState(): void {
    this._emergencyPressed = false;
    this._hardwareErrorCount = 0;
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  /**
   * Increments the 302 counter and shows the modal when the threshold is reached.
   * Emits a CUSS log and New Relic event per occurrence for production traceability.
   *
   * @param source - The channel that reported the 302 ('statusChange' | 'statusIsOK')
   */
  registerHardwareError(source: 'statusChange' | 'statusIsOK'): void {
    this._hardwareErrorCount++;
    const modalShown = this._hardwareErrorCount >= SSBD_HARDWARE_ERROR_THRESHOLD;

    console.warn(
      `[SsbdEmergencyService] 302 counted: ${this._hardwareErrorCount}/${SSBD_HARDWARE_ERROR_THRESHOLD} (source: ${source})`
    );

    // Log to CUSS application log
    try {
      const devMgr = this.cussService.getDeviceManager();
      if (devMgr) {
        devMgr
          .getAppManager()
          .sendApplicationLog(
            100,
            `CDS_APPLOG,302,ssbd hardware error ${this._hardwareErrorCount}/${SSBD_HARDWARE_ERROR_THRESHOLD}`
          );
      }
    } catch {
      /* best-effort */
    }

    // Emit New Relic event for production traceability
    try {
      this.newRelicEvents.trackEvent(NEW_RELIC_EVENT.SSBD_HARDWARE_ERROR_COUNTED, {
        code: 302,
        occurrence: this._hardwareErrorCount,
        threshold: SSBD_HARDWARE_ERROR_THRESHOLD,
        source,
        emergency_pressed: this._emergencyPressed,
        modal_shown: modalShown,
      });
    } catch {
      /* best-effort — NR failure must not break the flow */
    }

    if (modalShown) {
      this.modalService.show('emergency');
    }
  }

  /**
   * Clears the emergency state: resets counter and flag, closes modal if open.
   */
  private clearEmergencyState(): void {
    this._hardwareErrorCount = 0;
    this._emergencyPressed = false;
    if (this.modalService.currentVariant === 'emergency') {
      this.modalService.hide('emergency');
    }
  }

  /**
   * Sends the kiosk to Out of Service mode via OOSManager.
   * Only called for code 305 (NOT_RESPONDING).
   */
  private triggerOos(code: number): void {
    try {
      const devMgr = this.cussService.getDeviceManager();
      if (devMgr) {
        devMgr
          .getAppManager()
          .sendApplicationLog(100, `CDS_APPLOG,${code},ssbd device not responding - going OOS`);
      }
      const oosMgr = this.cussService.getOOSManager();
      if (oosMgr) {
        oosMgr.completeTransaction(true);
      }
    } catch (err) {
      console.error('[SsbdEmergencyService] Error triggering OOS:', err);
    }
  }
}
